import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { LeadForm } from '../components/forms/LeadForm';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { ArrowRight, Search, FileCheck, Key, Home, Phone, Calendar, MapPin, DollarSign, BedDouble, Building2, Shield, Lock } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const IDX_IFRAME = "https://sef.mlsmatrix.com/Matrix/public/IDX.aspx?idx=dbf21e14";

const buyingSteps = [
  { icon: Search, title: 'Property Search', description: 'Browse MLS listings and get personalized recommendations.' },
  { icon: FileCheck, title: 'Pre-Approval', description: 'Get pre-approved to strengthen your offer.' },
  { icon: Home, title: 'Home Tours', description: 'Visit properties with your agent.' },
  { icon: Key, title: 'Close & Move In', description: 'Smooth closing and keys in hand.' }
];

export const Buyers = () => {
  const [filters, setFilters] = useState({ location: '', minPrice: '', maxPrice: '', beds: '', propertyType: '' });
  const [idxUnlocked, setIdxUnlocked] = useState(false);
  const [gateForm, setGateForm] = useState({ name: '', email: '', phone: '' });
  const [gateLoading, setGateLoading] = useState(false);

  const handleUnlockIDX = async (e) => {
    e.preventDefault();
    if (!gateForm.name || !gateForm.email) { toast.error('Name and email required'); return; }
    setGateLoading(true);
    try {
      await axios.post(`${API}/leads`, { ...gateForm, source: 'buyer', message: 'Unlocked property search' });
      setIdxUnlocked(true);
      toast.success("Search unlocked! Browse properties below.");
    } catch { toast.error("Something went wrong."); }
    finally { setGateLoading(false); }
  };

  const handleFilterSearch = () => {
    const el = document.getElementById('idx-iframe');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div data-testid="buyers-page">
      {/* Pre-Approval + Search Hero */}
      <section className="pt-8 pb-4 bg-[#0a0a0a]" data-testid="buyers-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          {/* Pre-Approval Banner */}
          <div className="bg-gradient-to-r from-[#D4AF37]/10 to-[#D4AF37]/5 border border-[#D4AF37]/30 rounded-xl p-4 sm:p-5 mb-6 flex flex-col sm:flex-row items-center justify-between gap-4" data-testid="preapproval-banner">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0">
                <Shield className="w-5 h-5 text-[#D4AF37]" />
              </div>
              <div>
                <p className="text-[#F5F5F0] font-semibold text-sm">Get Pre-Approved First</p>
                <p className="text-[#808080] text-xs">Know your budget and make stronger offers. FHA, VA, Conventional, DSCR available.</p>
              </div>
            </div>
            <Link to="/financing">
              <Button className="btn-gold text-sm font-semibold whitespace-nowrap" data-testid="preapproval-cta">
                Get Pre-Approved Now <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>

          <div className="text-center mb-6">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-light text-[#F5F5F0] mb-3" style={{ fontFamily: 'Playfair Display' }}>
              Find Your <span className="text-gold-gradient">Dream Home</span>
            </h1>
            <p className="text-base text-[#808080] max-w-xl mx-auto">
              Search South Florida properties below. Start your home search now.
            </p>
          </div>

          {/* Quick Filter Bar */}
          <div className="bg-[#111111] border border-[#D4AF37]/20 rounded-xl p-4 sm:p-6 mb-6" data-testid="property-search-filters">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              <div className="col-span-2 sm:col-span-1">
                <label className="text-[#808080] text-xs uppercase tracking-wider mb-1 block">Location</label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#D4AF37]" />
                  <input type="text" placeholder="City, ZIP..." value={filters.location} onChange={(e) => setFilters({...filters, location: e.target.value})}
                    className="w-full pl-9 pr-3 py-2.5 bg-[#1A1A1A] border border-[#333] rounded-lg text-[#F5F5F0] text-sm placeholder:text-[#808080] focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37]/20" data-testid="filter-location" />
                </div>
              </div>
              <div>
                <label className="text-[#808080] text-xs uppercase tracking-wider mb-1 block">Min Price</label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#D4AF37]" />
                  <select value={filters.minPrice} onChange={(e) => setFilters({...filters, minPrice: e.target.value})} className="w-full pl-9 pr-3 py-2.5 bg-[#1A1A1A] border border-[#333] rounded-lg text-[#F5F5F0] text-sm appearance-none" data-testid="filter-min-price">
                    <option value="">No Min</option>
                    {['100000','200000','300000','400000','500000','750000','1000000','1500000','2000000'].map(v => <option key={v} value={v}>${(+v/1000)}K</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-[#808080] text-xs uppercase tracking-wider mb-1 block">Max Price</label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#D4AF37]" />
                  <select value={filters.maxPrice} onChange={(e) => setFilters({...filters, maxPrice: e.target.value})} className="w-full pl-9 pr-3 py-2.5 bg-[#1A1A1A] border border-[#333] rounded-lg text-[#F5F5F0] text-sm appearance-none" data-testid="filter-max-price">
                    <option value="">No Max</option>
                    {['200000','300000','400000','500000','750000','1000000','1500000','2000000','5000000'].map(v => <option key={v} value={v}>${(+v >= 1000000) ? (+v/1000000)+'M' : (+v/1000)+'K'}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-[#808080] text-xs uppercase tracking-wider mb-1 block">Beds</label>
                <div className="relative">
                  <BedDouble className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#D4AF37]" />
                  <select value={filters.beds} onChange={(e) => setFilters({...filters, beds: e.target.value})} className="w-full pl-9 pr-3 py-2.5 bg-[#1A1A1A] border border-[#333] rounded-lg text-[#F5F5F0] text-sm appearance-none" data-testid="filter-beds">
                    <option value="">Any</option>
                    {['1','2','3','4','5'].map(v => <option key={v} value={v}>{v}+</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-[#808080] text-xs uppercase tracking-wider mb-1 block">Type</label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#D4AF37]" />
                  <select value={filters.propertyType} onChange={(e) => setFilters({...filters, propertyType: e.target.value})} className="w-full pl-9 pr-3 py-2.5 bg-[#1A1A1A] border border-[#333] rounded-lg text-[#F5F5F0] text-sm appearance-none" data-testid="filter-type">
                    <option value="">All Types</option>
                    <option value="single_family">Single Family</option>
                    <option value="condo">Condo</option>
                    <option value="townhouse">Townhouse</option>
                    <option value="multi_family">Multi-Family</option>
                    <option value="land">Land</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="mt-4 flex flex-col sm:flex-row gap-3 items-center justify-between">
              <Button onClick={handleFilterSearch} className="btn-gold w-full sm:w-auto px-8 py-3 text-base font-medium" data-testid="search-properties-btn">
                <Search className="w-5 h-5 mr-2" /> Search Properties
              </Button>
              <div className="flex gap-3">
                <a href="tel:7863560878">
                  <Button variant="outline" className="btn-outline-gold text-sm" data-testid="buyers-call-btn">
                    <Phone className="w-4 h-4 mr-1" /> Call Agent
                  </Button>
                </a>
                <a href="https://calendly.com/seantherealtor/15min" target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className="btn-outline-gold text-sm" data-testid="buyers-book-btn">
                    <Calendar className="w-4 h-4 mr-1" /> Book Tour
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* IDX Search with Lead Gate */}
      <section className="pb-8 bg-[#0a0a0a]" data-testid="idx-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          {!idxUnlocked ? (
            <div className="relative" data-testid="idx-lead-gate">
              {/* Blurred IDX Preview */}
              <div className="relative overflow-hidden rounded-xl" style={{ height: '400px' }}>
                <iframe src={IDX_IFRAME} title="MLS Property Search" className="w-full h-full pointer-events-none" style={{ filter: 'blur(6px)', opacity: '0.4' }} />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0a0a0a]/50 to-[#0a0a0a]" />
              </div>
              {/* Gate Overlay */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="bg-[#111111] border border-[#D4AF37]/30 rounded-xl p-6 sm:p-8 max-w-md w-full mx-4 shadow-2xl" data-testid="lead-gate-form">
                  <div className="text-center mb-5">
                    <Lock className="w-8 h-8 text-[#D4AF37] mx-auto mb-3" />
                    <h3 className="text-xl text-[#F5F5F0] font-medium" style={{ fontFamily: 'Playfair Display' }}>
                      Unlock Full Property Search
                    </h3>
                    <p className="text-[#808080] text-sm mt-1">Enter your info to access all MLS listings</p>
                  </div>
                  <form onSubmit={handleUnlockIDX} className="space-y-3">
                    <Input type="text" placeholder="Your Name *" value={gateForm.name} onChange={e => setGateForm({...gateForm, name: e.target.value})} required className="bg-[#1A1A1A] border-[#333] text-[#F5F5F0] placeholder:text-[#808080]" data-testid="gate-name" />
                    <Input type="email" placeholder="Email *" value={gateForm.email} onChange={e => setGateForm({...gateForm, email: e.target.value})} required className="bg-[#1A1A1A] border-[#333] text-[#F5F5F0] placeholder:text-[#808080]" data-testid="gate-email" />
                    <Input type="tel" placeholder="Phone (optional)" value={gateForm.phone} onChange={e => setGateForm({...gateForm, phone: e.target.value})} className="bg-[#1A1A1A] border-[#333] text-[#F5F5F0] placeholder:text-[#808080]" data-testid="gate-phone" />
                    <Button type="submit" className="btn-gold w-full py-3 font-semibold" disabled={gateLoading} data-testid="gate-submit">
                      {gateLoading ? 'Unlocking...' : 'Unlock Property Search'}
                    </Button>
                  </form>
                </div>
              </div>
            </div>
          ) : (
            <div className="idx-container" data-testid="idx-container" id="idx-iframe">
              <iframe src={IDX_IFRAME} title="MLS Property Search" className="w-full min-h-[700px]" loading="lazy" />
            </div>
          )}
        </div>
      </section>

      {/* Buying Process */}
      <section className="py-16 bg-[#111111]" data-testid="buying-process">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <p className="section-overline">The Process</p>
            <h2 className="section-title mt-2">How We Help You Buy</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {buyingSteps.map((step, index) => (
              <div key={index} className="relative">
                <div className="card-hover bg-[#0a0a0a] p-6 rounded-lg h-full">
                  <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mb-3">
                    <step.icon className="w-6 h-6 text-[#D4AF37]" />
                  </div>
                  <span className="text-[#D4AF37] text-xs font-semibold uppercase tracking-wider">Step {index + 1}</span>
                  <h3 className="text-lg font-light text-[#F5F5F0] mt-1.5 mb-2" style={{ fontFamily: 'Playfair Display' }}>{step.title}</h3>
                  <p className="text-[#808080] text-sm">{step.description}</p>
                </div>
                {index < buyingSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                    <ArrowRight className="w-5 h-5 text-[#D4AF37]/30" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lead Form + Pre-Approval Push */}
      <section className="py-20 bg-[#0a0a0a]" data-testid="buyers-lead-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <p className="section-overline">Ready to Buy?</p>
              <h2 className="section-title mt-2 mb-6">Let's Find Your Perfect Home</h2>
              <p className="text-[#808080] leading-relaxed mb-6">
                Tell us what you're looking for and we'll match you with the right properties.
              </p>
              <ul className="space-y-2 text-[#F5F5F0] mb-8">
                {['Access to off-market properties', 'Personalized search criteria', 'Expert negotiation support', 'Closing coordination'].map(b => (
                  <li key={b} className="flex items-center text-sm"><div className="w-2 h-2 rounded-full bg-[#D4AF37] mr-3" />{b}</li>
                ))}
              </ul>
              <div className="bg-[#111111] border border-[#D4AF37]/20 rounded-xl p-5 mb-6">
                <p className="text-[#D4AF37] font-semibold text-sm mb-1">Not pre-approved yet?</p>
                <p className="text-[#808080] text-xs mb-3">Strengthen your offers and know your exact budget. FHA, VA, Conventional, DSCR available through SMF.</p>
                <Link to="/financing">
                  <Button className="btn-gold w-full text-sm" data-testid="buyers-get-preapproved">
                    <Shield className="w-4 h-4 mr-2" /> Get Pre-Approved Now (SMF)
                  </Button>
                </Link>
              </div>
              <div className="flex gap-3">
                <a href="tel:7863560878"><Button className="btn-gold"><Phone className="w-4 h-4 mr-2" /> Call (786) 356-0878</Button></a>
                <a href="https://calendly.com/seantherealtor/15min" target="_blank" rel="noopener noreferrer"><Button variant="outline" className="btn-outline-gold"><Calendar className="w-4 h-4 mr-2" /> Book Tour</Button></a>
              </div>
            </div>
            <LeadForm 
              source="buyer"
              title="Buyer Inquiry"
              subtitle="Tell us about your dream home and we'll help you find it."
              buttonText="Start My Search"
              showPropertyInterest={true}
            />
          </div>
        </div>
      </section>
    </div>
  );
};
